import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Brain, Users, Heart, Compass, BookOpen, ArrowRight } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { FAQ, Disclaimer } from "../components/FAQ";
import { useAuth } from "../lib/auth";

const DIMENSIONS = [
  { w: "25%", en: "Critical Thinking", hi: "क्रिटिकल थिंकिंग", dEn: "12 timed items: logical reasoning, pattern recognition, decision making, analytical ability.", dHi: "12 समयबद्ध प्रश्न: तार्किक तर्क, पैटर्न पहचान, निर्णय क्षमता, विश्लेषणात्मक योग्यता।", icon: Brain },
  { w: "20%", en: "Personality Fit", hi: "व्यक्तित्व फ़िट", dEn: "Big Five (OCEAN) Likert items with reverse coding; work-readiness composite of Conscientiousness, Stability, Openness.", dHi: "Big Five (OCEAN) लिकर्ट प्रश्न, रिवर्स कोडिंग सहित; कर्तव्यनिष्ठा, स्थिरता और खुलेपन का कार्य-तत्परता समग्र।", icon: Users },
  { w: "20%", en: "Emotional Intelligence", hi: "भावनात्मक बुद्धिमत्ता", dEn: "Scenario-based items across self-awareness, self-regulation, empathy, motivation and social skills.", dHi: "आत्म-जागरूकता, आत्म-नियमन, सहानुभूति, प्रेरणा और सामाजिक कौशल पर परिदृश्य-आधारित प्रश्न।", icon: Heart },
  { w: "20%", en: "Career Alignment", hi: "करियर संरेखण", dEn: "RIASEC profile derived from your traits, matched against an India-specific career database.", dHi: "आपके गुणों से निकाला गया RIASEC प्रोफ़ाइल, भारत-विशिष्ट करियर डेटाबेस से मिलान।", icon: Compass },
  { w: "15%", en: "Learning Readiness", hi: "सीखने की तत्परता", dEn: "Openness + Conscientiousness + reasoning: how fast you can acquire new skills.", dHi: "खुलापन + कर्तव्यनिष्ठा + तर्क: आप कितनी तेज़ी से नए कौशल सीख सकते हैं।", icon: BookOpen },
];

export default function Knowledge() {
  const { i18n } = useTranslation();
  const isHi = i18n.language?.startsWith("hi");
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-background">
      <Nav />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12 lg:py-16" data-testid="knowledge-page">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
          <div className="label-eyebrow">{isHi ? "नॉलेज हब · कार्यप्रणाली" : "Knowledge Hub · Methodology"}</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            {isHi ? "BharatIQ Score के पीछे का विज्ञान।" : "The science behind the BharatIQ Score."}
          </h1>
          <p className="mt-4 text-muted-foreground text-sm sm:text-base leading-relaxed">
            {isHi
              ? "एक संख्या, 0–1000, पाँच आयामों से बनी। स्कोरिंग पूरी तरह नियम-आधारित और पारदर्शी है; AI केवल आपके परिणामों की व्याख्या और सिफ़ारिशें लिखता है।"
              : "One number, 0–1000, built from five dimensions. Scoring is fully rule-based and transparent; AI only writes the explanation and recommendations on top of your numbers."}
          </p>
        </motion.div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          {DIMENSIONS.map((d, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="rounded-xl border border-border bg-white p-5" data-testid={`dimension-${i}`}>
              <div className="flex items-center justify-between">
                <d.icon className="w-5 h-5 text-primary" />
                <span className="font-display text-xl font-extrabold text-accent">{d.w}</span>
              </div>
              <div className="mt-4 font-display font-bold">{isHi ? d.hi : d.en}</div>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{isHi ? d.dHi : d.dEn}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h2 className="font-display text-base md:text-lg font-bold">{isHi ? "अक्सर पूछे जाने वाले प्रश्न" : "Frequently asked questions"}</h2>
            <div className="mt-4"><FAQ /></div>
          </div>
          <div className="lg:col-span-5 space-y-4">
            <div className="rounded-xl border border-border bg-white p-6">
              <h3 className="font-display font-bold">{isHi ? "फ्रेमवर्क" : "Frameworks we map to"}</h3>
              <ul className="mt-3 space-y-2 text-sm text-foreground/80">
                <li>• Big Five Personality (OCEAN)</li>
                <li>• Holland's RIASEC career interest model</li>
                <li>• Critical-thinking frameworks (reasoning, pattern recognition, decision making)</li>
                <li>• Emotional-intelligence frameworks (self-awareness, regulation, empathy, motivation, social skills)</li>
              </ul>
              <p className="mt-4 text-xs text-muted-foreground">{isHi ? "प्रश्न बैंक, स्कोरिंग और सिफ़ारिशें समय के साथ अद्यतन की जाती हैं।" : "Question banks, scoring and recommendations are versioned and updated as the platform evolves."}</p>
            </div>
            <Disclaimer />
            <Link to={user ? "/test" : "/auth?mode=signup"} data-testid="knowledge-start-btn" className="btn-primary w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md font-semibold">
              {isHi ? "मूल्यांकन शुरू करें" : "Start the assessment"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
